import { Button } from "@/components/ui/Button";
import { useCurationAgentMessage } from "@/features/data-curation/hooks/useCurationAgentMessage";
import { cn } from "@/utils/cn";
import { Check, Circle, CircleSlash, Hand, Loader2 } from "lucide-react";
import { useState } from "react";
import { useMasterListCoverage } from "../hooks";
import {
  currentStage,
  formatCount,
  percent,
  readPapers,
  STAGE_APPROVALS,
  STAGES,
  stageRecord,
} from "../stages";
import type { Coverage, DatasetBuilderProgress, StageId, StageStatus } from "../types";
import { ColumnsStage } from "./ColumnsStage";
import { CoverageStage } from "./CoverageStage";
import { MasterListStage } from "./MasterListStage";
import { ReadingStage } from "./ReadingStage";
import { SearchStage } from "./SearchStage";

type Props = {
  progress: DatasetBuilderProgress | undefined;
  datasetId: string | null;
};

const STATUS_LABEL: Record<StageStatus | "not_started", string> = {
  running: "Running",
  waiting: "Waiting for you",
  done: "Done",
  skipped: "Skipped",
  not_started: "Not started",
};

const StatusIcon = ({ status }: { status: StageStatus | "not_started" }) => {
  if (status === "done") return <Check className="h-3.5 w-3.5" aria-hidden />;
  if (status === "skipped") return <CircleSlash className="h-3.5 w-3.5" aria-hidden />;
  if (status === "running") {
    return <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" aria-hidden />;
  }
  if (status === "waiting") return <Hand className="h-3.5 w-3.5" aria-hidden />;
  return <Circle className="h-3.5 w-3.5" aria-hidden />;
};

const summary = (
  stage: StageId,
  progress: DatasetBuilderProgress | undefined,
  coverage: Coverage | undefined,
): string | null => {
  if (stage === "columns") {
    const data = stageRecord(progress, "columns")?.data;
    if (!data?.columns.length) return null;
    return `${data.columns.length} columns${data.locked ? " · locked" : ""}`;
  }
  if (stage === "search") {
    const kept = stageRecord(progress, "search")?.data.revisions.filter((item) => item.kept);
    const best = kept?.[kept.length - 1];
    return best ? `P ${percent(best.precision, 1)} · R ${percent(best.recall, 1)}` : null;
  }
  if (stage === "master_list") {
    const data = stageRecord(progress, "master_list")?.data;
    return data?.papers != null ? `${formatCount(data.papers)} papers` : null;
  }
  if (stage === "reading") {
    return coverage ? `${percent(readPapers(coverage), coverage.papers)} read` : null;
  }
  const data = stageRecord(progress, "coverage")?.data;
  return data?.complete ? "Complete" : null;
};

/** The stages of a Comprehensive Dataset Builder run, and a view of each stage. */
export const ComprehensiveProgress = ({ progress, datasetId }: Props) => {
  const coverageQuery = useMasterListCoverage(datasetId, progress);
  const [picked, setPicked] = useState<StageId | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const sendMessage = useCurationAgentMessage(progress?.session_id);
  if (!progress?.stages.length) return null;
  const selected = picked ?? currentStage(progress);
  const coverage = coverageQuery.data;
  const panel = STAGES.find((stage) => stage.id === selected)!;
  const record = stageRecord(progress, selected);
  const approval = STAGE_APPROVALS[selected];

  return (
    <section
      aria-labelledby="comprehensive-progress-title"
      className="rounded-[3px] border border-zinc-200 bg-white"
    >
      <div className="border-b border-zinc-100 px-4 py-3">
        <h2 id="comprehensive-progress-title" className="text-sm font-semibold text-zinc-900">
          Full read run
        </h2>
        <p className="text-xs text-zinc-500">
          Schema optimization, then config optimization, then the read of every paper. The
          agent runs only the stages of the run plan that you approve.
        </p>
      </div>
      <ol className="grid gap-px bg-zinc-100 sm:grid-cols-5" aria-label="Stages of the run">
        {STAGES.map((stage, index) => {
          const status = stageRecord(progress, stage.id)?.status ?? "not_started";
          const detail = status === "skipped" ? null : summary(stage.id, progress, coverage);
          const isSelected = stage.id === selected;
          return (
            <li key={stage.id} className="bg-white">
              <button
                type="button"
                aria-pressed={isSelected}
                aria-controls="comprehensive-stage-panel"
                onClick={() => setPicked(stage.id)}
                className={cn(
                  "flex h-full w-full flex-col items-start gap-1 border-b-2 px-4 py-3 text-left motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-400",
                  isSelected ? "border-zinc-900 bg-zinc-50" : "border-transparent hover:bg-zinc-50",
                )}
              >
                <span className="text-2xs font-medium uppercase tracking-wide text-zinc-500">
                  {index + 1}. {stage.label}
                </span>
                <span
                  className={cn(
                    "flex items-center gap-1.5 text-xs font-medium",
                    status === "done" && "text-emerald-700",
                    status === "running" && "text-blue-700",
                    status === "waiting" && "text-amber-700",
                    (status === "not_started" || status === "skipped") && "text-zinc-400",
                  )}
                >
                  <StatusIcon status={status} />
                  {STATUS_LABEL[status]}
                </span>
                {detail && <span className="text-xs text-zinc-700">{detail}</span>}
              </button>
            </li>
          );
        })}
      </ol>
      <div
        id="comprehensive-stage-panel"
        role="region"
        aria-label={panel.label}
        className="space-y-3 px-4 py-4"
      >
        <p className="text-xs text-zinc-500">{panel.description}</p>
        {record?.status === "skipped" ? (
          <p className="text-sm text-zinc-600">
            The run plan skips this stage. Ask the agent in the chat to run it.
          </p>
        ) : (
          <>
            {record?.data.now && record.status !== "done" && (
              <p className="text-sm text-zinc-800" aria-live="polite">
                <span className="font-medium text-zinc-900">Now: </span>
                {record.data.now}
              </p>
            )}
            {record?.status === "waiting" && approval && (
              <div className="flex flex-col gap-2 rounded-[3px] border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 sm:flex-row sm:items-center sm:justify-between">
                <p>{approval.text}</p>
                {sent === `${selected}:${record.updated_at}` ? (
                  <p role="status" className="shrink-0 font-medium">
                    Sent to the agent
                  </p>
                ) : (
                  <Button
                    size="sm"
                    className="shrink-0"
                    disabled={!progress.session_id}
                    onClick={() => {
                      sendMessage(approval.message);
                      setSent(`${selected}:${record.updated_at}`);
                    }}
                  >
                    {approval.label}
                  </Button>
                )}
              </div>
            )}
            {selected === "columns" && record && (
              <ColumnsStage data={stageRecord(progress, "columns")!.data} />
            )}
            {selected === "search" && record && (
              <SearchStage data={stageRecord(progress, "search")!.data} />
            )}
            {selected === "master_list" && record && (
              <MasterListStage data={stageRecord(progress, "master_list")!.data} />
            )}
            {selected === "reading" && record && (
              <ReadingStage
                coverage={coverage}
                loading={coverageQuery.isLoading}
                error={coverageQuery.isError}
              />
            )}
            {selected === "coverage" && (record || coverage?.complete) && (
              <CoverageStage data={stageRecord(progress, "coverage")?.data} coverage={coverage} />
            )}
            {!record && !(selected === "coverage" && coverage?.complete) && (
              <p className="text-sm text-zinc-500">This stage has not started.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
};
